import React, { useState } from 'react';
import type { ApartmentEditNavText } from '../../i18n/pages/apartmentEditNav';
import {
  CHECKLIST_OPEN_KEY, ChecklistItem, ChecklistItemId, ChecklistSummary, defaultChecklistOpen,
} from './setupChecklist';

interface Props {
  items: ChecklistItem[];
  summary: ChecklistSummary;
  text: ApartmentEditNavText;
  onJump: (item: ChecklistItem) => void;
}

const RADIUS = 15;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const itemLabel = (t: ApartmentEditNavText, id: ChecklistItemId): string => ({
  calendar: t.ckCalendar,
  ubyportLogin: t.ckUbyportLogin,
  ubyportAuto: t.ckUbyportAuto,
  keyAccess: t.ckKeyAccess,
  wifi: t.ckWifi,
  guideEnabled: t.ckGuideEnabled,
  stripe: t.ckStripe,
}[id]);

function readStoredOpen(): boolean | null {
  try {
    const v = window.localStorage.getItem(CHECKLIST_OPEN_KEY);
    return v === null ? null : v === '1';
  } catch {
    return null;
  }
}

function storeOpen(open: boolean) {
  try {
    window.localStorage.setItem(CHECKLIST_OPEN_KEY, open ? '1' : '0');
  } catch {
    /* a UI preference only; the page works without it */
  }
}

const Ring: React.FC<{ summary: ChecklistSummary; label: string }> = ({ summary, label }) => {
  const { loading, done, total, allDone } = summary;
  const filled = loading ? 0 : (done / total) * CIRCUMFERENCE;
  const colour = allDone ? '#15803d' : '#2563eb';
  return (
    <span className="ae-ring" role="img" aria-label={label}>
      <svg viewBox="0 0 36 36" width="32" height="32" aria-hidden="true" focusable="false">
        <circle cx="18" cy="18" r={RADIUS} fill="none" stroke="#e2e8f0" strokeWidth="4" />
        <circle
          cx="18" cy="18" r={RADIUS} fill="none" stroke={colour} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={`${filled.toFixed(2)} ${CIRCUMFERENCE.toFixed(2)}`} transform="rotate(-90 18 18)"
        />
        <text x="18" y="21.8" textAnchor="middle" fontSize="11" fontWeight="700" fill={allDone ? '#15803d' : '#1a365d'}>
          {loading ? '…' : allDone ? '✓' : `${done}/${total}`}
        </text>
      </svg>
    </span>
  );
};

/**
 * The "what is still missing?" line above the tabs: one row with a progress ring, expandable into
 * the seven items, each of which jumps to the field that fixes it.
 *
 * <p>Open by default. The host's own choice — either way — is remembered per browser and wins from
 * then on; a finished checklist has nothing to show and cannot be opened.
 */
const SetupChecklist: React.FC<Props> = ({ items, summary, text, onJump }) => {
  const [stored, setStored] = useState<boolean | null>(readStoredOpen);
  const { loading, done, total, allDone } = summary;

  const wantOpen = stored ?? defaultChecklistOpen();
  const open = wantOpen && !allDone && !loading;

  const title = loading ? text.checklistLoading : allDone ? text.checklistAllDone : text.checklistProgress(done, total);
  const ringLabel = loading ? text.checklistAriaLoading : allDone ? text.checklistAriaAllDone : text.checklistAriaProgress(done, total);
  const toggle = () => {
    const next = !open;
    setStored(next);
    storeOpen(next);
  };

  return (
    <section className={`ae-card ae-checklist${allDone ? ' ae-alldone' : ''}`} aria-label={text.checklistRegion}>
      <h2 className="ae-checklist-h">
        <button
          type="button" className="ae-checklist-toggle" aria-expanded={open} aria-controls="ae-checklist-body"
          onClick={toggle} disabled={allDone || loading}
        >
          <span className="ae-checklist-main">
            <Ring summary={summary} label={ringLabel} />
            <b className="ae-checklist-title">{title}</b>
          </span>
          {!allDone && !loading && <span className="ae-checklist-go">{open ? text.checklistHide : text.checklistShow}</span>}
        </button>
      </h2>
      {open && (
        <div id="ae-checklist-body" className="ae-checklist-body">
          <ul className="ae-checklist-list">
            {items.map(item => (
              <li key={item.id}>
                <button type="button" className={`ae-ck ae-ck-${item.status}`} onClick={() => onJump(item)}>
                  <span className="ae-ck-mark" aria-hidden="true">
                    {item.status === 'done' ? '✓' : item.status === 'na' ? '–' : '○'}
                  </span>
                  <span className="ae-ck-label">{itemLabel(text, item.id)}</span>
                  <span className="ae-ck-state">
                    {item.status === 'done' ? text.statusDone : item.status === 'na' ? text.statusNa : text.statusTodo}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <p className="ae-hint">{text.checklistHint}</p>
        </div>
      )}
    </section>
  );
};

export default SetupChecklist;
